import { zodResolver } from '@hookform/resolvers/zod';
import { getCountryDataList } from 'countries-list';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import _PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { saveShippingDetails } from '../actions/cartActions';
import CheckoutStepper from '../components/CheckoutStepper';

const PhoneInput = _PhoneInput.default || _PhoneInput;

const COUNTRY_LIST = getCountryDataList()
  .map(({ iso2, name }) => ({ code: iso2, name }))
  .sort((a, b) => a.name.localeCompare(b.name));

const DIAL_CODES = Object.fromEntries(
  getCountryDataList().map(({ iso2, phone }) => [iso2, String(phone[0])])
);

const safeText = /^[\p{L}\p{N}\s\-'.,#/()+&]+$/u;

const getSchema = (t) =>
  z.object({
    email: z.string().email(t('shipping.validEmail')),
    phoneNumber: z.string().min(7, t('shipping.validPhone')),
    fullName: z
      .string()
      .min(2, t('shipping.nameRequired'))
      .max(100)
      .regex(/^[\p{L}\s\-'.]+$/u, t('shipping.nameChars')),
    country: z.string().min(1, t('shipping.countryRequired')),
    address: z
      .string()
      .min(3, t('shipping.addressRequired'))
      .max(200)
      .regex(safeText, t('shipping.addressChars')),
    city: z
      .string()
      .min(2, t('shipping.cityRequired'))
      .max(100)
      .regex(/^[\p{L}\s\-'.]+$/u, t('shipping.cityChars')),
    postalCode: z
      .string()
      .min(3, t('shipping.postalRequired'))
      .max(20)
      .regex(/^[\w\s\-]+$/, t('shipping.postalInvalid')),
  });

export default function ShippingScreen(props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cart = useSelector((state) => state.cart);
  const { cartItems, shippingDetails } = cart;

  const [phoneCountry, setPhoneCountry] = useState(
    shippingDetails.country ? shippingDetails.country.toLowerCase() : 'pt'
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(getSchema(t)),
    mode: 'onBlur',
    defaultValues: {
      email: shippingDetails.email || '',
      phoneNumber: shippingDetails.phoneNumber || '',
      fullName: shippingDetails.fullName || '',
      country: shippingDetails.country || '',
      address: shippingDetails.address || '',
      city: shippingDetails.city || '',
      postalCode: shippingDetails.postalCode || '',
    },
  });

  useEffect(() => {
    if (cartItems.length <= 0) navigate('/cart');
  }, [cartItems, navigate]);

  useEffect(() => {
    if (shippingDetails.country) return;
    fetch('/api/geo')
      .then((res) => res.json())
      .then(({ country }) => {
        if (!country || country.length !== 2) return;
        const code = country.toUpperCase();
        const dialCode = DIAL_CODES[code];
        const currentPhone = getValues('phoneNumber');
        reset(
          {
            ...getValues(),
            country: code,
            ...(!currentPhone && dialCode ? { phoneNumber: `+${dialCode}` } : {}),
          },
          { keepDirty: true, keepTouched: true, keepErrors: true }
        );
        setPhoneCountry(code.toLowerCase());
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (data) => {
    dispatch(
      saveShippingDetails({
        email: data.email,
        phoneNumber: data.phoneNumber,
        fullName: data.fullName,
        address: data.address,
        city: data.city,
        postalCode: data.postalCode,
        country: data.country,
      })
    );
    navigate('/cart/billing');
  };

  return (
    <section className="shipping">
      <div className="shipping-container">
        <div className="shipping-inner">
          <CheckoutStepper
            steps={[t('stepper.shipping'), t('stepper.billing'), t('stepper.review')]}
            activeStep={0}
          />
          <form className="shipping-form" onSubmit={handleSubmit(onSubmit)}>
            <h2>{t('shipping.title')}</h2>
            <div>
              <label htmlFor="email">{t('shipping.email')}</label>
              <input type="email" id="email" maxLength={254} {...register('email')} />
              {errors.email && <span className="field-error">{errors.email.message}</span>}
            </div>
            <div>
              <label>{t('shipping.phone')}</label>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    country={phoneCountry}
                    value={field.value}
                    onChange={(value) => field.onChange(value ? `+${value}` : value)}
                    inputProps={{ id: 'phoneNumber' }}
                    containerClass="phone-input-container"
                    inputClass="phone-input-field"
                  />
                )}
              />
              {errors.phoneNumber && (
                <span className="field-error">{errors.phoneNumber.message}</span>
              )}
            </div>
            <div>
              <label htmlFor="fullName">{t('shipping.fullName')}</label>
              <input type="text" id="fullName" maxLength={100} {...register('fullName')} />
              {errors.fullName && <span className="field-error">{errors.fullName.message}</span>}
            </div>
            <div>
              <label htmlFor="country">{t('shipping.country')}</label>
              <Controller
                name="country"
                control={control}
                render={({ field }) => (
                  <select
                    id="country"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                  >
                    <option value="">{t('shipping.selectCountry')}</option>
                    {COUNTRY_LIST.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.country && <span className="field-error">{errors.country.message}</span>}
            </div>
            <div>
              <label htmlFor="address">{t('shipping.address')}</label>
              <input type="text" id="address" maxLength={200} {...register('address')} />
              {errors.address && <span className="field-error">{errors.address.message}</span>}
            </div>
            <div>
              <label htmlFor="city">{t('shipping.city')}</label>
              <input type="text" id="city" maxLength={85} {...register('city')} />
              {errors.city && <span className="field-error">{errors.city.message}</span>}
            </div>
            <div>
              <label htmlFor="postalCode">{t('shipping.postalCode')}</label>
              <input type="text" id="postalCode" maxLength={20} {...register('postalCode')} />
              {errors.postalCode && (
                <span className="field-error">{errors.postalCode.message}</span>
              )}
            </div>
            <div>
              <button className="primary" type="submit">
                {t('shipping.continue')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
